import React from 'react';
import { Link } from 'react-router-dom';
import { Event } from '../../types/event';
import { formatDate } from '../../utils/dateFormatter';

interface EventCardProps {
  event: Event;
  useSlug?: boolean;
}

const EventCard: React.FC<EventCardProps> = ({ event, useSlug }) => {
  const isUpcoming = new Date(event.start_date.toString()) > new Date();

  return (
    <article className="relative rounded-xl overflow-hidden shadow-lg bg-white/10 border border-white/10 backdrop-blur-md hover:scale-[1.02] transition-transform duration-200">
      {/* Image avec overlay et lien */}
      <Link to={useSlug && event.slug ? `/events/${event.slug}` : `/events/${event.id}`} className="block relative h-48 group">
        <img 
          src={event.image_url || '/images/event-placeholder.jpg'} 
          alt={`Image de l'événement: ${event.title}`} 
          className="w-full h-full object-cover object-center transition-transform duration-300 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
        {isUpcoming && (
          <div className="absolute top-2 right-2 bg-green-500/90 text-white text-xs font-bold px-2 py-1 rounded shadow-lg" aria-label="Événement à venir">
            À venir
          </div>
        )}
      </Link>
      {/* Contenu */}
      <div className="p-5 flex flex-col gap-2">
        <Link 
          to={useSlug && event.slug ? `/events/${event.slug}` : `/events/${event.id}`}
          className="text-2xl font-bold text-white mb-1 truncate hover:text-yellow-400 transition-colors"
          id={`event-title-${event.id}`}
        >
          {event.title}
        </Link>
        <div className="flex items-center text-gray-300 mb-1" aria-label="Date de l'événement">
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
          </svg>
          <span className="text-sm font-medium">{formatDate(event.start_date.toString())}</span>
        </div>
        {event.location && (
          <div className="flex items-center text-gray-400 mb-1" aria-label="Lieu de l'événement">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
            </svg>
            <span className="text-sm truncate">{event.location}</span>
          </div>
        )}
        {event.description && (
          <div 
            className="text-gray-200 line-clamp-3 mb-4" 
            dangerouslySetInnerHTML={{ __html: event.description }} 
          />
        )}
        <div className="flex justify-between items-end mt-2">
          {event.contest && (
            <span className="text-xs text-yellow-400 font-semibold" aria-label="Concours associé">
              {event.contest.name}
            </span>
          )}
          <Link 
            to={useSlug && event.slug ? `/events/${event.slug}` : `/events/${event.id}`}
            className="inline-flex items-center justify-center px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-black font-medium rounded-lg transition-colors"
          >
            Voir les billets
          </Link>
        </div>
      </div>
    </article>
  );
};

export default EventCard;
